import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, Tooltip, Typography, Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import workoutService from '../services/workoutService';

const PAGE_SIZE = 20;

const AcceptedWorkouts = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [total, setTotal] = useState(0);

  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [filterBy, setFilterBy] = useState('title');
  const [filterStatusBy, setFilterStatusBy] = useState('all');
  const [dateFilterStart, setDateFilterStart] = useState('');
  const [dateFilterEnd, setDateFilterEnd] = useState('');
  const [page, setPage] = useState(1);

  const [showMediaModal, setShowMediaModal] = useState(false);
  const [modalMediaType, setModalMediaType] = useState(null);
  const [modalMediaUrl, setModalMediaUrl] = useState('');
  const [modalYoutubeVideoId, setModalYoutubeVideoId] = useState(null);

  const fetchWorkouts = useCallback(async (pageNum = 1, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const {
        keyword = appliedQuery,
        filterField = filterBy,
        status = filterStatusBy,
        dateStart = dateFilterStart,
        dateEnd = dateFilterEnd,
      } = filters;

      const { items, total: totalCount } = await workoutService.getPendingWorkouts({
        skip: (pageNum - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        keyword,
        filterBy: filterField,
        status,
        dateStart,
        dateEnd,
      });

      setWorkouts(items);
      setTotal(totalCount);
      setPage(pageNum);
    } catch (err) {
      setError('Failed to fetch workouts: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, filterBy, filterStatusBy, dateFilterStart, dateFilterEnd]);

  useEffect(() => {
    dispatch(login(user));
    fetchWorkouts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccept = async (workoutId) => {
    try { await workoutService.approveWorkout(workoutId); await fetchWorkouts(page); }
    catch (err) { alert('Failed to accept workout: ' + (err?.message || 'Unknown error')); }
  };

  const handleDecline = async (workoutId) => {
    try { await workoutService.declineWorkout(workoutId); await fetchWorkouts(page); }
    catch (err) { alert('Failed to decline workout: ' + (err?.message || 'Unknown error')); }
  };

  const handleRemove = async (workoutId) => {
    if (!window.confirm('Remove this workout?')) return;
    try { await workoutService.removeWorkout(workoutId); await fetchWorkouts(page); }
    catch (err) { alert('Failed to remove workout: ' + (err?.message || 'Unknown error')); }
  };

  const performSearch = () => {
    const trimmed = (query || '').trim();
    setAppliedQuery(trimmed);
    fetchWorkouts(1, {
      keyword: trimmed,
      filterField: filterBy,
      status: filterStatusBy,
      dateStart: dateFilterStart,
      dateEnd: dateFilterEnd,
    });
  };

  const handleRemoveFilter = () => {
    setQuery('');
    setAppliedQuery('');
    setFilterStatusBy('all');
    setDateFilterStart('');
    setDateFilterEnd('');
    fetchWorkouts(1, {
      keyword: '',
      filterField: filterBy,
      status: 'all',
      dateStart: '',
      dateEnd: '',
    });
  };

  const isFilteredActive = () => {
    if ((appliedQuery || '').trim()) return true;
    if (dateFilterStart || dateFilterEnd) return true;
    if (filterStatusBy !== 'all') return true;
    return false;
  };

  const handlePageChange = (_, v) => {
    fetchWorkouts(v);
  };

  const exportExcel = async () => {
    try {
      const { items } = await workoutService.getPendingWorkouts({
        skip: 0,
        limit: Math.min(Math.max(total, PAGE_SIZE), 5000),
        keyword: appliedQuery,
        filterBy,
        status: filterStatusBy,
        dateStart: dateFilterStart,
        dateEnd: dateFilterEnd,
      });
      if (!items || items.length === 0) return;
      const rows = items.map((w, index) => ({
        No: index + 1,
        Title: w.title || '-',
        Athlete: w?.owner?.name || '-',
        Email: w?.owner?.email || '-',
        Category: w.category || '-',
        Sec: (typeof w.duration === 'number') ? w.duration.toFixed(2) : w.duration || '-',
        'Uploaded At': w?.createdAt ? new Date(w.createdAt).toLocaleString() : '-',
        Video: w.videoUrl || '-',
        Image: w.promotionalImageUrl || '-',
        Status: w?.newRequest === true ? 'Pending' : w?.isApproved === true ? 'Accepted' : 'Rejected',
      }));
      const csvContent = 'data:text/csv;charset=utf-8,' +
        [Object.keys(rows[0]).join(','), ...rows.map((r) => Object.values(r).map((v) => `"${v}"`).join(','))].join('\n');
      const link = document.createElement('a');
      link.setAttribute('href', encodeURI(csvContent));
      link.setAttribute('download', `accepted_workouts_${new Date().toISOString()}.csv`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (err) {
      alert('Failed to export: ' + (err?.message || 'Unknown error'));
    }
  };

  const openMedia = (url, type, youtubeVideoId) => {
    if (!url && !youtubeVideoId) return;
    setModalMediaUrl(url || '');
    setModalMediaType(type);
    setModalYoutubeVideoId(youtubeVideoId || null);
    setShowMediaModal(true);
  };
  const closeModal = () => { setShowMediaModal(false); setModalMediaUrl(''); setModalMediaType(null); setModalYoutubeVideoId(null); };
  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const getStatusChip = (w) => {
    if (w?.newRequest !== false) return <Chip label="Pending" size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ffb74d', fontWeight: 600 }} />;
    if (w?.isApproved === true) return <Chip label="Accepted" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.15)', color: '#81c784', fontWeight: 600 }} />;
    return <Chip label="Rejected" size="small" sx={{ bgcolor: 'rgba(244,67,54,0.15)', color: '#e57373', fontWeight: 600 }} />;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  if (loading && workouts.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>;
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Field</InputLabel>
          <Select value={filterBy} label="Field" onChange={(e) => setFilterBy(e.target.value)}>
            <MenuItem value="title">Title</MenuItem>
            <MenuItem value="owner">Athlete</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatusBy} label="Status" onChange={(e) => {
            const status = e.target.value;
            setFilterStatusBy(status);
            fetchWorkouts(1, {
              keyword: appliedQuery,
              filterField: filterBy,
              status,
              dateStart: dateFilterStart,
              dateEnd: dateFilterEnd,
            });
          }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && performSearch()} sx={{ minWidth: 180 }} />
        <Typography variant="caption" sx={{ color: '#8b949e', mx: 0.5 }}>Uploaded:</Typography>
        <TextField size="small" type="date" value={dateFilterStart} onChange={(e) => {
          const dateStart = e.target.value;
          setDateFilterStart(dateStart);
          fetchWorkouts(1, {
            keyword: appliedQuery,
            filterField: filterBy,
            status: filterStatusBy,
            dateStart,
            dateEnd: dateFilterEnd,
          });
        }} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
        <Typography variant="caption" sx={{ color: '#8b949e' }}>–</Typography>
        <TextField size="small" type="date" value={dateFilterEnd} onChange={(e) => {
          const dateEnd = e.target.value;
          setDateFilterEnd(dateEnd);
          fetchWorkouts(1, {
            keyword: appliedQuery,
            filterField: filterBy,
            status: filterStatusBy,
            dateStart: dateFilterStart,
            dateEnd,
          });
        }} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
        <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={performSearch}>Search</Button>
        <Button size="small" variant="outlined" color="error" startIcon={<ClearIcon />} onClick={handleRemoveFilter} disabled={!isFilteredActive()}>Clear</Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportExcel}>Export</Button>
        <IconButton size="small" onClick={() => fetchWorkouts(page)} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.25)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#2bb6a9' }} />
          </Box>
        )}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Athlete</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>IG</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell>Video</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workouts.length === 0 ? (
                <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4, color: '#8b949e' }}>No workouts found.</TableCell></TableRow>
              ) : workouts.map((w, index) => {
                const title = w.title || '-';
                const email = w?.owner?.email || '-';
                const ig = (w?.owner?.instagramUsername || '').replace(/^@/, '');
                const igUrl = ig ? `https://instagram.com/${ig}` : '';
                const uploadedAtDisplay = w?.createdAt
                  ? new Date(w.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
                  : '—';
                return (
                  <TableRow key={w._id}>
                    <TableCell>{startIndex + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={title}><Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Typography></Tooltip>
                        <IconButton size="small" onClick={() => copyToClipboard(title)} sx={{ color: '#8b949e', p: 0.3 }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{w?.owner?.name || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={email}><Typography variant="body2" sx={{ fontSize: '0.8rem', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</Typography></Tooltip>
                        {email !== '-' && <IconButton size="small" onClick={() => copyToClipboard(email)} sx={{ color: '#8b949e', p: 0.3 }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{w.category || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{typeof w.duration === 'number' ? w.duration.toFixed(2) : '-'}</TableCell>
                    <TableCell>{ig ? <a href={igUrl} target="_blank" rel="noreferrer" style={{ color: '#2bb6a9', fontSize: '0.8rem', textDecoration: 'none' }}>@{ig}</a> : '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{uploadedAtDisplay}</TableCell>
                    <TableCell>
                      {w.sourceType === 'youtube' && w.youtubeVideoId ? (
                        <Chip label="YouTube" size="small" onClick={() => openMedia(null, 'video', w.youtubeVideoId)} sx={{ bgcolor: 'rgba(255,0,0,0.15)', color: '#ff4444', fontWeight: 600, cursor: 'pointer' }} />
                      ) : w.videoUrl ? <IconButton size="small" onClick={() => openMedia(w.videoUrl, 'video')} sx={{ color: '#ff9800' }}><PlayCircleOutlineIcon fontSize="small" /></IconButton> : <Typography variant="body2" sx={{ color: '#8b949e' }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      {w.promotionalImageUrl ? <IconButton size="small" onClick={() => openMedia(w.promotionalImageUrl, 'image')} sx={{ color: '#4caf50' }}><ImageIcon fontSize="small" /></IconButton> : <Typography variant="body2" sx={{ color: '#8b949e' }}>—</Typography>}
                    </TableCell>
                    <TableCell>{getStatusChip(w)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" variant="contained" color="error" onClick={() => handleDecline(w._id)} disabled={!(w?.newRequest !== false || w?.isApproved === true)} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>Reject</Button>
                        <Button size="small" variant="contained" color="success" onClick={() => handleAccept(w._id)} disabled={!(w?.newRequest !== false || w?.isApproved === false)} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>Accept</Button>
                        <IconButton size="small" onClick={() => handleRemove(w._id)} sx={{ color: '#8b949e' }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#8b949e' }}>
            Showing {total === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + PAGE_SIZE, total)} of {total} entries
          </Typography>
          <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} size="small"
            sx={{ '& .MuiPaginationItem-root': { color: '#8b949e' }, '& .Mui-selected': { bgcolor: 'rgba(43,182,169,0.15) !important', color: '#2bb6a9' } }} />
        </Box>
      )}

      <Dialog open={showMediaModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {modalMediaType === 'video' ? 'Video' : 'Image'} Preview
          <IconButton onClick={closeModal} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {modalYoutubeVideoId ? (
            <iframe
              width="100%"
              height="400"
              src={`https://www.youtube.com/embed/${modalYoutubeVideoId}?autoplay=1&rel=0`}
              title="YouTube Preview"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
              style={{ borderRadius: 8, maxHeight: '70vh' }}
            />
          ) : modalMediaType === 'video' ? (
            <video controls style={{ width: '100%', maxHeight: '70vh', borderRadius: 8 }}><source src={modalMediaUrl} /></video>
          ) : modalMediaType === 'image' ? (
            <img src={modalMediaUrl} alt="Preview" style={{ width: '100%', objectFit: 'contain', maxHeight: '70vh', borderRadius: 8 }} />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AcceptedWorkouts;
